import clsx from 'clsx';
import styles from './styles.module.scss';
import { useOrientation } from '@/hooks';
import { useConfigStore, useCategoryTypeStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import { useCategoryTypeBuildingOptions, useCategoryTypeFloorList } from '@/hooks/poi/useCategoryTypeData';
import { CATEGORY_TYPE_SEARCH_MODE } from '@/enums/category-type';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import ShopFnbFacilityFloorButton from '@/components/common/shop-fnb-facility/buttons/floor';
import ShopFnbFacilityBuildingDropdown from '@/components/common/shop-fnb-facility/building-dropdown';
import { AppUtils } from '@/utils/app';

const CategoryTypeSearchByFloor = () => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const selectedFloor = useCategoryTypeStore((state) => state.selectedFloor);
    const selectedBuildingId = useCategoryTypeStore((state) => state.selectedBuildingId);
    const menuId = useCategoryTypeStore((state) => state.menuId);
    const setSelectedFloor = useCategoryTypeStore((state) => state.setSelectedFloor);
    const setSelectedBuildingId = useCategoryTypeStore((state) => state.setSelectedBuildingId);
    const updateCategoryTypeContents = useCategoryTypeStore((state) => state.updateCategoryTypeContents);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);

    const buildingOptions = useCategoryTypeBuildingOptions(t);
    const floorList = useCategoryTypeFloorList(selectedBuildingId, t, menuId ?? undefined);

    const handleSelectFloor = (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] }) => {
        setSelectedFloor(floor as any);
        updateCategoryTypeContents(CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    const handleSelectBuilding = (buildingId: string) => {
        const allFloorOption = {
            id: 'all',
            label: t('category_type.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };
        setSelectedBuildingId(buildingId);
        setSelectedFloor(allFloorOption as Parameters<typeof setSelectedFloor>[0]);
        updateCategoryTypeContents(CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    return (
        <div>
        <div
            className={clsx(styles['search-by-floor'], {
                [styles['search-by-floor--single-building']]: buildingOptions.length === 1,
                [styles['search-by-floor--vertical']]: !isHorizontal,
                [styles['search-by-floor--vertical__halfmode']]: !isHorizontal && screenMode === 'split',
            })}
        >
            {buildingOptions.length > 1 && (
                <div
                    className={clsx(styles['search-by-floor__dropdown'], {
                        [styles['search-by-floor__dropdown--vertical-halfmode']]: !isHorizontal && screenMode === 'split',
                    })}
                >
                    <ShopFnbFacilityBuildingDropdown defaultValue={selectedBuildingId || 'all'} options={buildingOptions} onSelect={handleSelectBuilding} />
                </div>
            )}
            <div
                className={clsx(
                    styles['search-by-floor__floors'],
                    {
                        [styles['search-by-floor__floors--single-building']]: buildingOptions.length === 1,
                        [styles['search-by-floor__floors--vertical']]: !isHorizontal,
                        [styles['search-by-floor__floors--vertical-halfmode']]: !isHorizontal && screenMode === 'split',
                    },
                    'scrollbar-custom-gray-99938f scrollbar-w-4 overflow-y-auto'
                )}
                data-region-nav
            >
                {floorList.map((floor, index) => {
                    return (
                        <ShopFnbFacilityFloorButton
                            tabIndex={9 + index}
                            data-interactive
                            role="radio"
                            type="button"
                            key={floor.id}
                            classes={floor.id === 'all' ? 'text' : 'number'}
                            aria-label={
                                floor.id === 'all'
                                    ? t('category_type.search.tts.content.all_floors')
                                    : t('category_type.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(floor.label) })
                            }
                            title={floor.label}
                            aria-checked={floor.id === selectedFloor?.id}
                            onClick={() => handleSelectFloor(floor)}
                            isVertical={!isHorizontal}
                        />
                    );
                })}
            </div>
        </div>
        </div>
    );
};

export default CategoryTypeSearchByFloor;
